import { useCallback } from 'react';
import { useFormData } from '../model/render-form.store';
import { useRenderSettingsActions } from '../model/render-form-store.selectors';
import type { EnvironmentSetting } from '@/shared/constants';
import type { RenderControl } from '@/types/render';

// Dedicated hook for form field management
export function useRenderForm() {
  const formData = useFormData();
  const actions = useRenderSettingsActions();

  const handlePromptChange = useCallback(
    (field: 'concept' | 'angle', value: string) => {
      const key = field === 'concept' ? 'prompt_concept' : 'prompt_angle';
      actions.updateFormData({
        prompts: { ...formData.prompts, [key]: value },
      });
    },
    [actions, formData.prompts]
  );

  const handleControlsChange = useCallback(
    (controls: RenderControl[]) => {
      actions.updateFormData({
        controls: controls.length > 0 ? controls : null,
      });
    },
    [actions]
  );

  const handleStyleChange = useCallback(
    (styleIds: number[] | null) => {
      const sortedStyleIds = styleIds
        ? [...styleIds].sort((a, b) => a - b)
        : null;
      actions.updateFormData({ style_ids: sortedStyleIds });
    },
    [actions]
  );

  const handleMaterialsChange = useCallback(
    (materialIds: number[] | null) => {
      actions.updateFormData({ material_ids: materialIds });
    },
    [actions]
  );

  const handleColorsChange = useCallback(
    (colors: string[] | null) => {
      actions.updateFormData({ colors });
    },
    [actions]
  );

  const handleOutputStyleChange = useCallback(
    (styleId: number | null) => {
      actions.updateFormData({ output_style_id: styleId });
    },
    [actions]
  );

  const handleEnvironmentChange = useCallback(
    (setting: EnvironmentSetting) => {
      actions.updateFormData({ environment_setting: setting });
    },
    [actions]
  );

  return {
    // State
    formData,

    // Field handlers
    setPrompt: handlePromptChange,
    setControls: handleControlsChange,
    setStyles: handleStyleChange,
    setMaterials: handleMaterialsChange,
    setColors: handleColorsChange,
    setOutputStyle: handleOutputStyleChange,
    setEnvironment: handleEnvironmentChange,

    // Generic update
    updateFormData: actions.updateFormData,
    setDefaultValues: actions.setDefaultValues,
    resetForm: actions.resetFormData,
  };
}
